import { Box, Button } from "@chakra-ui/react"
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"

/**
 * TruncatedHTML Component
 *
 * A layout-aware HTML truncation component that intelligently truncates WYSIWYG content
 * without showing partial blocks, images, or breaking words. Uses DOM measurement and
 * manipulation to achieve clean truncation boundaries.
 *
 * Features:
 * - Height-based truncation without partial blocks
 * - Optional inline ellipsis on final text block
 * - Gradient overlay with "More" button when ellipsis isn't applicable
 * - ResizeObserver integration for responsive behavior
 * - Measurement callbacks for parent components
 */

// -----------------------------
// Type Definitions
// -----------------------------

/** Media element types that should be treated as atomic units */
type MediaElementTag = "IMG" | "PICTURE" | "VIDEO" | "IFRAME"

/** TreeWalker filter function for text nodes */
type TextNodeFilter = (node: Node) => number

/** Measurement data emitted to parent components */
export interface TruncationMeasure {
  naturalHeight: number
  renderedHeight: number
  truncated: boolean
}

/** Callback type for measurement events */
export type onMeasureType = (info: TruncationMeasure) => void

/** Configuration for the truncation algorithm */
interface TruncationConfig {
  html: string
  maxHeight: number
  reserveBottomPx: number
  ellipsis: boolean
  onTruncatedChange?: (truncated: boolean) => void
  onMeasure?: onMeasureType
}

/** Binary search result for text trimming */
interface TextTrimResult {
  bestLength: number
  didTruncate: boolean
}

// -----------------------------
// DOM Utilities
// -----------------------------

/**
 * Determines if an element represents media content that should be treated atomically
 * @param el - The element to check
 * @returns true if the element is a media element
 */
function isMediaElement(
  el: Element
): el is Element & { tagName: MediaElementTag } {
  const mediaTags: MediaElementTag[] = ["IMG", "PICTURE", "VIDEO", "IFRAME"]
  return mediaTags.includes(el.tagName as MediaElementTag)
}

/**
 * Creates a TreeWalker that traverses only visible text nodes
 * @param root - The root element to traverse
 * @returns A TreeWalker configured for visible text nodes
 */
function createVisibleTextWalker(root: Element): TreeWalker {
  const filter: TextNodeFilter = (node) => {
    // Reject empty or whitespace-only text nodes
    if (!node.nodeValue || !node.nodeValue.trim()) {
      return NodeFilter.FILTER_REJECT
    }

    // Reject nodes without proper parent elements
    if (!(node.parentElement instanceof Element)) {
      return NodeFilter.FILTER_REJECT
    }

    // Reject nodes in hidden elements
    const style = getComputedStyle(node.parentElement)
    if (style.display === "none" || style.visibility === "hidden") {
      return NodeFilter.FILTER_REJECT
    }

    return NodeFilter.FILTER_ACCEPT
  }

  return document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: filter
  })
}

/**
 * Counts the total number of characters across all visible text nodes
 * @param root - The root element to count characters in
 * @returns Total character count
 */
function getTextLength(root: Element): number {
  const walker = createVisibleTextWalker(root)
  let length = 0
  let node: Node | null

  // eslint-disable-next-line no-cond-assign
  while ((node = walker.nextNode())) {
    length += (node.nodeValue || "").length
  }

  return length
}

/**
 * Finds the optimal word boundary for text truncation
 * @param text - The text to find a boundary in
 * @param targetIndex - The desired truncation point
 * @returns The index of the nearest word boundary
 */
function findWordBoundary(text: string, targetIndex: number): number {
  if (targetIndex <= 0) return 0
  if (targetIndex >= text.length) return text.length

  // Search backwards up to 16 characters for a whitespace character
  const searchRange = Math.max(0, targetIndex - 16)
  for (let i = targetIndex; i >= searchRange; i--) {
    if (/\s/.test(text[i])) return i
  }

  return targetIndex
}

/**
 * Truncates text content to approximately the specified character count,
 * snapping to word boundaries to avoid breaking words
 * @param root - The element containing text to truncate
 * @param targetLength - The approximate number of characters to keep
 */
function truncateTextContent(root: Element, targetLength: number): void {
  const walker = createVisibleTextWalker(root)
  let remainingChars = targetLength
  let node: Node | null

  while ((node = walker.nextNode())) {
    const nodeText = node.nodeValue || ""

    if (remainingChars >= nodeText.length) {
      remainingChars -= nodeText.length
      continue
    }

    // Truncate this node and remove all following content
    const slicePoint = findWordBoundary(nodeText, remainingChars)
    node.nodeValue = nodeText.slice(0, slicePoint)
    removeFollowingNodes(node)
    break
  }
}

/**
 * Removes all nodes that follow the given node within the truncation scope
 * @param node - The node after which to remove siblings
 */
function removeFollowingNodes(node: Node): void {
  const truncationRoot = findTruncationRoot(node)

  // Remove siblings at each level up to the truncation root
  let currentNode: Node | null = node
  while (currentNode && currentNode !== truncationRoot) {
    const parent: Node | null = currentNode.parentNode
    if (!parent) break

    // Remove all following siblings
    let sibling: Node | null = currentNode.nextSibling
    while (sibling) {
      const nextSibling: Node | null = sibling.nextSibling
      parent.removeChild(sibling)
      sibling = nextSibling
    }

    currentNode = parent
  }

  // Handle the case where we're at the truncation root level
  if (currentNode === truncationRoot && truncationRoot.firstChild) {
    const pathToNode = getPathToNode(node, truncationRoot)
    const topmostNode =
      pathToNode.length > 0 ? pathToNode[pathToNode.length - 1] : node

    let sibling: Node | null = topmostNode.nextSibling
    while (sibling) {
      const nextSibling: Node | null = sibling.nextSibling
      ;(topmostNode.parentNode as Node).removeChild(sibling)
      sibling = nextSibling
    }
  }
}

/**
 * Finds the truncation root element (marked with data-truncate-root)
 * @param node - The node to start searching from
 * @returns The truncation root element
 */
function findTruncationRoot(node: Node): Element {
  let current: Node | null = node

  // Search up the tree for the truncation root
  while (current) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      const element = current as Element
      if (element.hasAttribute("data-truncate-root")) {
        return element
      }
    }
    current = current.parentNode
  }

  // Fallback: find the nearest element ancestor
  let ancestor: Node | null = node
  while (ancestor && ancestor.nodeType !== Node.ELEMENT_NODE) {
    ancestor = ancestor.parentNode
  }

  return (ancestor as Element) || (node as Element)
}

/**
 * Gets the path from a node to its truncation root
 * @param node - The starting node
 * @param root - The truncation root
 * @returns Array of nodes in the path
 */
function getPathToNode(node: Node, root: Node): Node[] {
  const path: Node[] = []
  let current: Node | null = node

  while (current && current !== root) {
    path.push(current)
    current = current.parentNode
  }

  return path
}

/**
 * Attempts to apply an inline ellipsis to the last visible text node
 * @param root - The element containing the text
 * @param container - The container element for height measurement
 * @returns true if ellipsis was successfully applied
 */
function applyInlineEllipsis(root: Element, container: HTMLElement): boolean {
  const walker = createVisibleTextWalker(root)
  let lastTextNode: Node | null = null
  let node: Node | null

  // Find the last visible text node
  while ((node = walker.nextNode())) {
    lastTextNode = node
  }

  if (!lastTextNode) return false

  const originalText = lastTextNode.nodeValue || ""
  const trimmedText = originalText.replace(/\s+$/g, "")

  if (trimmedText.length === 0) return false

  // Try progressively shorter versions with ellipsis
  const maxBacktrack = Math.min(16, trimmedText.length)
  for (let charsToRemove = 1; charsToRemove <= maxBacktrack; charsToRemove++) {
    const ellipsisText =
      trimmedText.slice(0, Math.max(0, trimmedText.length - charsToRemove)) +
      "…"
    lastTextNode.nodeValue = ellipsisText

    // Check if it fits within the container
    if (container.scrollHeight <= container.clientHeight + 1) {
      return true
    }
  }

  // Restore original text if ellipsis didn't work
  lastTextNode.nodeValue = originalText
  return false
}

// -----------------------------
// Text Trimming Algorithm
// -----------------------------

/**
 * Performs binary search to find the optimal text length that fits within the container
 * @param element - The element containing text to trim
 * @param container - The container for height measurement
 * @returns Result containing the best length and truncation status
 */
function findOptimalTextLength(
  element: HTMLElement,
  container: HTMLElement
): TextTrimResult {
  const totalTextLength = getTextLength(element)
  let low = 0
  let high = totalTextLength
  let bestLength = 0

  // Store original content for restoration
  const originalContent =
    element.getAttribute("data-full-html") || element.innerHTML
  if (!element.getAttribute("data-full-html")) {
    element.setAttribute("data-full-html", originalContent)
  }

  // Binary search for the optimal length
  while (low <= high) {
    const mid = Math.floor((low + high) / 2)

    // Restore original content and apply truncation
    element.innerHTML = originalContent
    truncateTextContent(element, mid)

    // Check if it fits
    if (container.scrollHeight <= container.clientHeight + 1) {
      bestLength = mid
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  // Apply the best result
  element.innerHTML = originalContent
  truncateTextContent(element, bestLength)

  return {
    bestLength,
    didTruncate: bestLength < totalTextLength
  }
}

// -----------------------------
// Hook: useLayoutAwareTruncation
// -----------------------------

/**
 * Custom hook that manages layout-aware HTML truncation
 *
 * This hook handles the complex logic of measuring content, applying truncation,
 * and managing state changes. It uses ResizeObserver for responsive behavior
 * and requestAnimationFrame for performance optimization.
 *
 * @param config - Configuration object containing all truncation parameters
 * @returns Object containing refs, state, and control functions
 */
function useLayoutAwareTruncation(config: TruncationConfig) {
  const {
    html,
    maxHeight,
    reserveBottomPx,
    ellipsis,
    onTruncatedChange,
    onMeasure
  } = config

  // Refs for DOM elements and state tracking
  const containerRef = useRef<HTMLDivElement>(null)
  const lastEmittedRef = useRef<TruncationMeasure | null>(null)
  const lastTruncatedRef = useRef<boolean>(false)
  const rafIdRef = useRef<number | null>(null)

  // Component state
  const [expanded, setExpanded] = useState(false)
  const [truncated, setTruncatedState] = useState(false)
  const [ellipsisApplied, setEllipsisApplied] = useState(false)

  /**
   * Updates the truncated state and notifies listeners if changed
   * @param newTruncated - The new truncated state
   */
  const setTruncated = (newTruncated: boolean): void => {
    if (lastTruncatedRef.current !== newTruncated) {
      lastTruncatedRef.current = newTruncated
      setTruncatedState(newTruncated)
      onTruncatedChange?.(newTruncated)
    }
  }

  /**
   * Emits measurement data to parent components, debounced to prevent excessive calls
   * @param measure - The measurement data to emit
   */
  const emitMeasure = (measure: TruncationMeasure): void => {
    const previous = lastEmittedRef.current
    if (
      !previous ||
      previous.naturalHeight !== measure.naturalHeight ||
      previous.renderedHeight !== measure.renderedHeight ||
      previous.truncated !== measure.truncated
    ) {
      onMeasure?.(measure)
      lastEmittedRef.current = measure
    }
  }

  /**
   * Initializes the container with HTML content and marks it for truncation
   */
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || expanded) return

    container.setAttribute("data-truncate-root", "true")
    container.innerHTML = html
  }, [html, expanded])

  /**
   * Main truncation effect that handles the complex algorithm
   */
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Handle expanded state - show full content
    if (expanded) {
      container.style.maxHeight = ""
      container.style.overflow = ""
      container.style.paddingBottom = ""
      setTruncated(false)
      setEllipsisApplied(false)
      emitMeasure({
        naturalHeight: container.scrollHeight,
        renderedHeight: container.clientHeight,
        truncated: false
      })
      return
    }

    /**
     * Core truncation algorithm that intelligently trims content to fit
     * within the specified height constraints
     */
    const performTruncation = (): void => {
      if (!container || !container.firstElementChild) return

      try {
        // Step 1: Reset DOM and measure natural height
        container.innerHTML = html
        container.setAttribute("data-truncate-root", "true")
        container.style.paddingBottom = "0px"
        container.style.maxHeight = ""
        container.style.overflow = "visible"

        const naturalHeight = container.scrollHeight

        // Step 2: Check if content already fits
        container.style.maxHeight = `${maxHeight}px`
        container.style.overflow = "hidden"

        if (container.scrollHeight <= container.clientHeight + 1) {
          setTruncated(false)
          setEllipsisApplied(false)
          emitMeasure({
            naturalHeight,
            renderedHeight: container.clientHeight,
            truncated: false
          })
          return
        }

        // Step 3: Apply truncation algorithm
        const truncationResult = applyTruncationAlgorithm(
          container,
          naturalHeight
        )

        // Step 4: Apply final styles and emit results
        container.style.maxHeight = `${maxHeight}px`
        container.style.paddingBottom = `${Math.max(
          0,
          Math.min(reserveBottomPx, maxHeight)
        )}px`

        setTruncated(truncationResult.didTruncate)
        setEllipsisApplied(truncationResult.ellipsisApplied)

        emitMeasure({
          naturalHeight,
          renderedHeight:
            container.clientHeight -
            Math.max(0, Math.min(reserveBottomPx, maxHeight)),
          truncated: truncationResult.didTruncate
        })
      } catch (error) {
        console.error("Truncation algorithm failed:", error)
        // Fallback: show truncated state without modification
        setTruncated(true)
        setEllipsisApplied(false)
      }
    }

    /**
     * Applies the truncation algorithm to fit content within height constraints
     * @param container - The container element
     * @param naturalHeight - The natural height of the content
     * @returns Result of the truncation operation
     */
    const applyTruncationAlgorithm = (
      container: HTMLElement,
      naturalHeight: number
    ): { didTruncate: boolean; ellipsisApplied: boolean } => {
      let didTruncate = false
      let ellipsisApplied = false
      let lastTrimmedElement: HTMLElement | null = null

      // Reserve space for controls
      const reservedSpace = Math.max(0, Math.min(reserveBottomPx, maxHeight))
      container.style.maxHeight = `${maxHeight - reservedSpace}px`

      const children = Array.from(container.children) as HTMLElement[]

      // Step 1: Hide trailing block elements (starting from the end, skipping first child)
      let childIndex = children.length - 1
      while (
        childIndex >= 1 &&
        container.scrollHeight > container.clientHeight + 1
      ) {
        children[childIndex].hidden = true
        didTruncate = true
        childIndex--
      }

      const firstHiddenIndex = childIndex + 1
      const lastVisibleIndex = firstHiddenIndex - 1
      const lastVisibleChild = children[lastVisibleIndex]

      // Handle case where all children except first are hidden
      if (!lastVisibleChild) {
        return { didTruncate: true, ellipsisApplied: false }
      }

      // Step 2: Try to fit the first hidden element
      const candidateElement = children[firstHiddenIndex]
      if (candidateElement) {
        candidateElement.hidden = false

        if (isMediaElement(candidateElement)) {
          // Media elements: hide if they don't fit
          if (container.scrollHeight > container.clientHeight + 1) {
            candidateElement.hidden = true
            didTruncate = true
          }
        } else {
          // Text elements: trim content using binary search
          if (container.scrollHeight > container.clientHeight + 1) {
            const trimResult = findOptimalTextLength(
              candidateElement,
              container
            )
            if (trimResult.didTruncate) {
              didTruncate = true
              lastTrimmedElement = candidateElement
            }
          }
        }
      }

      // Step 3: If still overflowing, trim the last visible element
      if (container.scrollHeight > container.clientHeight + 1) {
        let targetIndex = lastVisibleIndex
        while (
          targetIndex >= 0 &&
          container.scrollHeight > container.clientHeight + 1
        ) {
          const targetElement = children[targetIndex]

          if (isMediaElement(targetElement)) {
            targetElement.hidden = true
            didTruncate = true
            targetIndex--
            continue
          }

          const trimResult = findOptimalTextLength(targetElement, container)
          if (trimResult.didTruncate) {
            didTruncate = true
            lastTrimmedElement = targetElement
          }
          break
        }
      }

      // Step 4: Apply inline ellipsis if requested and applicable
      if (
        ellipsis &&
        lastTrimmedElement &&
        !isMediaElement(lastTrimmedElement)
      ) {
        ellipsisApplied = applyInlineEllipsis(lastTrimmedElement, container)
      }

      return { didTruncate, ellipsisApplied }
    }

    /**
     * Schedules truncation using requestAnimationFrame for performance
     */
    const scheduleTruncation = (): void => {
      if (rafIdRef.current !== null) return

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null
        performTruncation()
      })
    }

    // Set up ResizeObserver for responsive behavior
    const resizeObserver = new ResizeObserver(() => {
      scheduleTruncation()
    })
    resizeObserver.observe(container)

    // Perform initial truncation
    scheduleTruncation()

    // Cleanup function
    return () => {
      resizeObserver.disconnect()
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [
    expanded,
    html,
    maxHeight,
    reserveBottomPx,
    ellipsis,
    onTruncatedChange,
    onMeasure
  ])

  return {
    containerRef,
    expanded,
    setExpanded,
    truncated,
    ellipsisApplied
  } as const
}

// -----------------------------
// Component Props and Types
// -----------------------------

/** Props for the TruncatedHTML component */
export interface TruncatedHtmlProps {
  /** HTML content to truncate */
  html: string
  /** Maximum height in pixels before truncation occurs */
  maxHeight?: number
  /** Custom render function for the "More" button */
  renderMore?: (controls: { expand: () => void }) => React.ReactNode
  /** Whether to attempt inline ellipsis on text elements */
  ellipsis?: boolean
  /** Callback for measurement events */
  onMeasure?: onMeasureType
  /** Additional CSS class name */
  className?: string
  /** Additional inline styles */
  style?: React.CSSProperties
}

/** Controls object passed to renderMore function */
export interface MoreButtonControls {
  /** Function to expand the truncated content */
  expand: () => void
}

// -----------------------------
// Main Component
// -----------------------------

/**
 * TruncatedHTML Component
 *
 * A sophisticated HTML truncation component that intelligently truncates WYSIWYG content
 * without showing partial blocks, images, or breaking words. Features include:
 *
 * - Height-based truncation with clean boundaries
 * - Optional inline ellipsis for text elements
 * - Gradient overlay with custom "More" button
 * - Responsive behavior with ResizeObserver
 * - Measurement callbacks for parent components
 *
 * @param props - Component props
 * @returns JSX element
 */
export default function TruncatedHTML(props: TruncatedHtmlProps): JSX.Element {
  const {
    html,
    maxHeight = 240,
    renderMore,
    ellipsis = false,
    onMeasure,
    className,
    style
  } = props

  // State for measuring control button height
  const [controlsHeight, setControlsHeight] = useState<number>(0)
  const measureRef = useRef<HTMLDivElement>(null)

  /**
   * Default "More" button component
   * @param controls - Control functions
   * @returns Default button element
   */
  const renderDefaultMoreButton = (
    controls: MoreButtonControls
  ): JSX.Element => (
    <Button onClick={controls.expand} size="sm" variant="outline">
      … More
    </Button>
  )

  // Initialize truncation hook
  const { containerRef, expanded, setExpanded, truncated, ellipsisApplied } =
    useLayoutAwareTruncation({
      html,
      maxHeight,
      reserveBottomPx: controlsHeight,
      ellipsis,
      onMeasure
    })

  /**
   * Creates a measurement element for the "More" button (hidden off-screen)
   * @returns React element for measurement
   */
  const createMeasurementElement = (): React.ReactNode => {
    const element = renderMore
      ? renderMore({ expand: () => {} })
      : renderDefaultMoreButton({ expand: () => {} })

    return React.isValidElement(element)
      ? React.cloneElement(element, { key: "measure" })
      : element
  }

  /**
   * Creates the visible "More" button element
   * @returns React element for display
   */
  const createVisibleElement = (): React.ReactNode => {
    const element = renderMore
      ? renderMore({ expand: () => setExpanded(true) })
      : renderDefaultMoreButton({ expand: () => setExpanded(true) })

    return React.isValidElement(element)
      ? React.cloneElement(element, { key: "visible" })
      : element
  }

  // Measure control button height for space reservation
  useLayoutEffect(() => {
    const measurementNode = measureRef.current
    if (!measurementNode) return

    const measureHeight = (): void => {
      setControlsHeight(measurementNode.offsetHeight || 0)
    }

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(measureHeight)
    })

    resizeObserver.observe(measurementNode)
    measureHeight()

    return () => resizeObserver.disconnect()
  }, [renderMore])

  // Determine if overlay should be shown
  const shouldShowOverlay =
    !expanded && truncated && (!ellipsis || !ellipsisApplied)

  return (
    <Box className={className} h="100%" style={style} position="relative">
      {/* Hidden measurement container for control button */}
      <Box
        className="TruncatedHTML-measure"
        position="absolute"
        left="-99999px"
        top="-99999px"
        visibility="hidden"
        pointerEvents="none"
        ref={measureRef}
      >
        {createMeasurementElement()}
      </Box>

      {/* Main content container (innerHTML managed by hook) */}
      <Box
        ref={containerRef}
        className="TruncatedHTML-content"
        aria-live="polite"
        aria-atomic={false}
        display="flow-root"
      />

      {/* Gradient overlay for visual truncation indication */}
      {shouldShowOverlay && (
        <Box
          aria-hidden
          position="absolute"
          left={0}
          right={0}
          bottom={controlsHeight}
          height="3rem"
          pointerEvents="none"
          bgGradient="linear(to-b, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 60%, rgba(255,255,255,1) 100%)"
        />
      )}

      {/* Visible "More" button */}
      {!expanded && truncated && (
        <Box position="absolute" left={0} right={0} bottom={0} pt={2}>
          {createVisibleElement()}
        </Box>
      )}

      {/* Expanded content (when user clicks "More") */}
      {expanded && <Box dangerouslySetInnerHTML={{ __html: html }} />}
    </Box>
  )
}
